import { useEffect, type ReactNode } from 'react';
import { Box, Group, Stack, Text, Skeleton } from '@mantine/core';
import { useConsoleNav } from '../shell/nav';
import {
  AutoGrid,
  ChipRail,
  EstatePill,
  MeterRow,
  NotConnected,
  PageHeader,
  Pill,
  ProviderPill,
  SectionCard,
  StatCard,
  StatSkeleton,
  ChartSkeleton,
  RowsSkeleton,
} from '../ui';
import { latency, listCost, num, pct, tokens as fmtTokens } from '../ui/format';
import { tokens as T } from '../theme/tokens';
import { useUIStore } from '../stores/uiStore';
import { panelPhase, useAnalyticsStore, type Panel } from '../stores/analyticsStore';
import { getSources } from '../stores/sources';
import { poolKeyLapsed, poolKeySelectable, splitTenantKey, type RangeKey } from '../data/types';
import { LegendSwatch, MODEL_COLOR, ModelStackChart, ThroughputChart } from '../features/charts';
import { usePoolStore, useTenantsStore } from '../stores/dataStores';
import { isFirstLoad, rowsOf } from '../stores/collection';

const RANGES: { value: RangeKey; label: string }[] = [
  { value: '24 hours', label: '24 hours' },
  { value: '7 days', label: '7 days' },
  { value: '30 days', label: '30 days' },
];

export function OverviewPage() {
  const navigate = useConsoleNav();
  const scope = useUIStore((s) => s.scope);
  const range = useUIStore((s) => s.range);
  const setUI = useUIStore((s) => s.set);
  const a = useAnalyticsStore();
  const tenants = rowsOf(useTenantsStore((s) => s.data));
  const pool = usePoolStore((s) => s.data);
  const loadPool = usePoolStore((s) => s.load);
  const scoped = scope ? splitTenantKey(scope) : null;
  const scopedTenant = scope ? tenants.find((t) => t.key === scope) : undefined;

  // Every mount, as the pool table does — not only when unconfigured. The
  // pool health card judges a lapse as of the snapshot it holds, and a probe
  // or a delete on another screen refreshes ONE row of that snapshot while
  // the rest age; coming back here must bring the whole pool current, or an
  // old row could read as lapsed against a newer neighbour's clock. Rows stay
  // on screen while it loads.
  useEffect(() => {
    loadPool(undefined as void);
  }, [loadPool]);

  useEffect(() => {
    a.load({ range, ...(scoped ? { estateId: scoped.estateId, tenantId: scoped.tenantId } : {}) });
    // The query is keyed by exactly these two, and re-running on anything else
    // would restart a BigQuery scan for no reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, scope]);

  // "Not connected" is a claim about the BACKEND, so it is made from the
  // SOURCE, not from whether a request happens to have finished.
  //
  // Keying it on the store's `wired` flag was wrong twice over. `wired` only
  // becomes true inside load(), which React runs after the first paint — so
  // every mount painted "not connected" for a frame before asking anything.
  // And once a query was in flight it stayed wrong for as long as BigQuery
  // took, turning a slow scan into a claim that the route did not exist.
  //
  // The empty source is the honest signal, and it is known before any paint.
  const connected = getSources().query.kind !== 'empty';

  if (!connected) {
    return (
      <Stack gap={20}>
        <PageHeader
          title="Router overview"
          lede={LEDE}
          aside={<RangeRail value={range} onChange={(v) => setUI('range', v)} />}
        />
        <NotConnected what="the analytics warehouse" />
      </Stack>
    );
  }

  /** pending reports that a panel has nothing to show YET — see panelPhase. */
  const pending = (panel: Panel, data: unknown) =>
    panelPhase(data, a.errors[panel]) === 'pending';

  const t = a.totals;
  const totalsPending = pending('totals', a.totals);
  const poolKeys = rowsOf(pool);
  // isFirstLoad is the collection primitive's own answer to "has this ever
  // arrived": loading AND empty. A reload with rows already present is not a
  // first load, so the figures stay put.
  const poolPending = isFirstLoad(pool);
  const health = {
    selectable: poolKeys.filter((k) => poolKeySelectable(k)).length,
    rested: poolKeys.filter((k) => !poolKeySelectable(k)).length,
    // A subscription nobody is using is one to drop; the pool screen's whole
    // economic argument is this number next to the rested one.
    idle: poolKeys.filter((k) => k.kind !== 'api_key' && k.quotaUsedPercent < 30).length,
    failing: poolKeys.filter((k) => k.failureCount > 0).length,
    // A token EXPIRING is not a signal — an access token lives hours and the
    // sweep renews it as a matter of course, so the old "expiring within 7d"
    // row was lit every hour of every day. A token that has LAPSED on an
    // enabled rotating key is: the renewal is not happening (DR-57). Named
    // longest-lapsed first.
    //
    // Judged as of the READ, not the wall clock. The pool comes over HTTP,
    // once per visit, and an access token renewed server-side since then
    // still carries its old expiry here — against a live clock a tab left
    // open would raise the very false alarm this row replaced. Against the
    // read it says only what was true when the console last looked, which is
    // all any row on this card says.
    lapsed: (() => {
      const asOf = pool.updatedAt || Date.now();
      const worst = poolKeys
        .filter((k) => poolKeyLapsed(k, asOf))
        .sort((x, y) => (x.expiresAt ?? '').localeCompare(y.expiresAt ?? ''))[0];
      return worst ? `${worst.provider} · ${worst.label}` : null;
    })(),
  };

  return (
    <Stack gap={16}>
      <PageHeader
        title={scopedTenant ? `${scopedTenant.displayName} · usage` : 'Router overview'}
        lede={LEDE}
        aside={<RangeRail value={range} onChange={(v) => setUI('range', v)} />}
      />

      <AutoGrid min={180} gap={14} mt={8}>
        {/* A skeleton stands in only while the figure has never arrived. Once
            it has, a re-query leaves it on screen — an operator changing the
            range should not watch five numbers blink out and back. */}
        <StatCard
          label="COMPLETIONS"
          value={t ? num(t.completions) : totalsPending ? <StatSkeleton /> : '—'}
          sub={range}
        />
        <StatCard
          label="PROVIDER COST · LIST"
          value={t ? listCost(t.listCostUsd) : totalsPending ? <StatSkeleton /> : '—'}
          sub="not what estates charged"
        />
        <StatCard
          label="BILLABLE SHARE"
          value={t ? pct(t.billableShare) : totalsPending ? <StatSkeleton /> : '—'}
          sub={t ? `${pct(1 - t.billableShare)} attributed` : undefined}
        />
        <StatCard
          label="P95 LATENCY"
          value={t ? latency(t.p95LatencyMs) : totalsPending ? <StatSkeleton /> : '—'}
          sub={scoped ? 'this tenant' : 'fleet-wide, all providers'}
        />
        <StatCard
          label="BILLING CONFLICTS"
          value={t ? num(t.billingConflicts) : totalsPending ? <StatSkeleton /> : '—'}
          sub="same id, different payload"
          color={t && t.billingConflicts > 0 ? T.danger : undefined}
        />
      </AutoGrid>

      <SectionCard
        title="Token throughput"
        subtitle={`${scopedTenant ? scopedTenant.displayName : 'All estates'} · ${
          range === '24 hours' ? 'hourly' : 'daily'
        } · ${range}`}
        right={
          a.throughput ? (
            <Group gap={16} wrap="wrap">
              <LegendSwatch color={T.coral} label="Tokens in" value={fmtTokens(a.throughput.totals.tokensIn)} />
              <LegendSwatch color={T.teal} label="Tokens out" value={fmtTokens(a.throughput.totals.tokensOut)} />
              <LegendSwatch color={T.violet} dashed label="Cache write" value={fmtTokens(a.throughput.totals.cacheWrite)} />
              <LegendSwatch color={T.marigold} dashed label="Cache read" value={fmtTokens(a.throughput.totals.cacheRead)} />
              <Box
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: T.tealFg,
                  background: T.tealSoft,
                  padding: '5px 10px',
                  borderRadius: 999,
                }}
              >
                {pct(
                  a.throughput.totals.cacheRead /
                    (a.throughput.totals.cacheRead + a.throughput.totals.tokensIn),
                )}{' '}
                served from cache
              </Box>
            </Group>
          ) : null
        }
      >
        <Box mt={16}>
          {a.throughput ? (
            <ThroughputChart data={a.throughput} />
          ) : pending('throughput', a.throughput) ? (
            <ChartSkeleton height={180} />
          ) : (
            <ChartPlaceholder height={180} />
          )}
        </Box>
      </SectionCard>

      <SectionCard
        title="Tokens by model"
        subtitle="Keyed on the request's pinned model id, not the provider's dated echo"
        right={
          a.models ? (
            <Group gap={14} wrap="wrap">
              {a.models.models.map((m) => (
                <Group key={m.model} gap={7} wrap="nowrap">
                  <Box
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 3,
                      background: MODEL_COLOR[m.model] ?? T.faint,
                    }}
                  />
                  <Text ff="monospace" fz={12} c={T.ink2}>
                    {m.model}
                  </Text>
                  <Text fz={12} fw={800}>
                    {fmtTokens(m.tokens)}
                  </Text>
                  <Text fz={12} c={T.ink5}>
                    {pct(m.share)}
                  </Text>
                </Group>
              ))}
            </Group>
          ) : null
        }
      >
        <Box mt={16}>
          {a.models ? (
            <ModelStackChart data={a.models} />
          ) : pending('models', a.models) ? (
            <ChartSkeleton height={150} />
          ) : (
            <ChartPlaceholder height={150} />
          )}
        </Box>
      </SectionCard>

      <AutoGrid min={340} gap={16}>
        <SectionCard
          title="Usage by tenant"
          right={
            <Text fz={12} c={T.ink4}>
              list cost · completions
            </Text>
          }
        >
          <Stack gap={14} mt={16}>
            {pending('tenants', a.tenants) ? <RowsSkeleton rows={4} height={30} /> : null}
            {(a.tenants ?? []).map((u) => {
              const max = Math.max(...(a.tenants ?? []).map((x) => x.listCostUsd), 1);
              return (
                <MeterRow
                  key={`${u.estateId}/${u.tenantId}`}
                  onClick={() => navigate(`/estates/${u.estateId}/tenants/${u.tenantId}`)}
                  value={(u.listCostUsd / max) * 100}
                  color={u.billingMode === 'billable' ? T.coral : T.faint}
                  left={
                    <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                      <EstatePill estate={u.estateId} />
                      <Text fz={14} fw={700} truncate>
                        {u.displayName}
                      </Text>
                      <Text
                        fz={11}
                        fw={700}
                        c={u.billingMode === 'billable' ? T.coralDeep : T.ink4}
                      >
                        {u.billingMode}
                      </Text>
                    </Group>
                  }
                  right={
                    <Text fz={14} fw={700} style={{ whiteSpace: 'nowrap' }}>
                      {listCost(u.listCostUsd)}
                      {u.billingMode === 'billable' ? '' : ' (unbilled)'}
                    </Text>
                  }
                  after={
                    <Text fz={12} c={T.ink4} ta="right" style={{ minWidth: 62 }}>
                      {num(u.completions)}
                    </Text>
                  }
                />
              );
            })}
          </Stack>
        </SectionCard>

        <SectionCard title="Provider health">
          <Box
            mt={16}
            style={{
              display: 'grid',
              gridTemplateColumns: '96px 1fr 62px 62px 62px',
              gap: 10,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.09em',
              color: T.ink5,
            }}
          >
            <div>PROVIDER</div>
            <div>SHARE OF CALLS</div>
            <div style={{ textAlign: 'right' }}>P50</div>
            <div style={{ textAlign: 'right' }}>P95</div>
            <div style={{ textAlign: 'right' }}>ERR</div>
          </Box>
          <Stack gap={11} mt={12}>
            {pending('providers', a.providers) ? <RowsSkeleton rows={3} height={22} /> : null}
            {(a.providers ?? []).map((p) => (
              <Box
                key={p.provider}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '96px 1fr 62px 62px 62px',
                  gap: 10,
                  alignItems: 'center',
                }}
              >
                <div>
                  <ProviderPill provider={p.provider} size="md" />
                </div>
                <Box
                  style={{
                    height: 7,
                    borderRadius: 999,
                    background: T.page,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    style={{
                      height: 7,
                      borderRadius: 999,
                      width: `${p.shareOfCalls * 100}%`,
                      background: T.teal,
                    }}
                  />
                </Box>
                <Text fz={13} ta="right" c={T.ink2}>
                  {latency(p.p50Ms)}
                </Text>
                <Text fz={13} ta="right" c={T.ink2}>
                  {latency(p.p95Ms)}
                </Text>
                <Text
                  fz={13}
                  ta="right"
                  fw={700}
                  c={p.errorRate >= 0.02 ? T.danger : T.ink2}
                >
                  {pct(p.errorRate, 1)}
                </Text>
              </Box>
            ))}
          </Stack>
        </SectionCard>

        <SectionCard
          title="Pool health"
          right={
            <Text fz={12} fw={700} c={T.coral} style={{ cursor: 'pointer' }} onClick={() => navigate('/pool')}>
              Open pool
            </Text>
          }
        >
          <Stack gap={12} mt={16}>
            <PoolHealthRow
              label="Selectable now"
              value={
                poolKeys.length
                  ? `${health.selectable} of ${poolKeys.length}`
                  : poolPending
                    ? <Skeleton height={14} width={60} radius="sm" />
                    : '—'
              }
            />
            <PoolHealthRow
              label="Rested / quota-paused"
              value={
                poolKeys.length
                  ? String(health.rested)
                  : poolPending
                    ? <Skeleton height={14} width={28} radius="sm" />
                    : '—'
              }
              color={health.rested > 0 ? T.danger : undefined}
            />
            <PoolHealthRow
              label="Idle below 30% for 14d"
              value={
                poolKeys.length
                  ? `${health.idle} subscription${health.idle === 1 ? '' : 's'}`
                  : poolPending
                    ? <Skeleton height={14} width={92} radius="sm" />
                    : '—'
              }
              color={health.idle > 0 ? T.marigoldFg : undefined}
            />
            <PoolHealthRow
              label="Refresh failures"
              value={
                poolKeys.length
                  ? String(health.failing)
                  : poolPending
                    ? <Skeleton height={14} width={28} radius="sm" />
                    : '—'
              }
              color={health.failing > 0 ? T.danger : undefined}
            />
            <PoolHealthRow
              label="Lapsed token"
              value={
                health.lapsed ??
                (poolKeys.length
                  ? 'none'
                  : poolPending
                    ? <Skeleton height={14} width={120} radius="sm" />
                    : '—')
              }
              color={health.lapsed ? T.danger : undefined}
            />
          </Stack>
          <Text fz={12} c={T.ink4} lh={1.6} mt={14}>
            A subscription that never crosses 30% utilisation is a subscription to drop; one that
            rests weekly is one to duplicate.
          </Text>
        </SectionCard>

        <SectionCard title="Auth realm mix" subtitle="Gates legacy-realm retirement — set RetireAfter when a realm reads zero">
          <Stack gap={13} mt={16}>
            {pending('realms', a.realms) ? <RowsSkeleton rows={2} height={26} /> : null}
            {(a.realms ?? []).map((r) => (
              <MeterRow
                key={r.realm}
                value={r.share * 100}
                color={r.native ? T.teal : T.marigold}
                left={
                  <Text ff="monospace" fz={13} c={T.ink2}>
                    {r.realm}
                  </Text>
                }
                right={
                  <Text fz={13} fw={700}>
                    {pct(r.share)}
                  </Text>
                }
              />
            ))}
          </Stack>
        </SectionCard>
      </AutoGrid>

      <SectionCard
        title="Reconciliation — prior UTC day"
        right={
          <Text fz={12} c={T.ink4}>
            ground truth joined to each estate's ledger on the billing event id
          </Text>
        }
      >
        <AutoGrid min={200} gap={16} mt={16}>
          {pending('recon', a.recon)
            ? // Cards, so the grid holds its shape rather than collapsing and
              // reflowing when the verdicts land.
              Array.from({ length: 2 }, (_, i) => <ChartSkeleton key={i} height={92} />)
            : null}
          {(a.recon ?? []).map((r) => (
            <Box
              key={r.estateId}
              style={{
                border: `1px solid ${r.ok ? T.okSoft : T.dangerBorder}`,
                background: r.ok ? T.okCard : T.dangerSoft,
                borderRadius: 12,
                padding: '14px 16px',
              }}
            >
              <Group gap={8}>
                <EstatePill estate={r.estateId} size="md" />
                <Text fz={13} fw={700} c={r.ok ? T.okFg : T.coralDeep}>
                  {r.verdict}
                </Text>
              </Group>
              <Text fz={13} c={T.neutralFg} mt={8} lh={1.5}>
                {r.detail}
              </Text>
            </Box>
          ))}
        </AutoGrid>
      </SectionCard>
    </Stack>
  );
}

function PoolHealthRow({
  label,
  value,
  color,
}: {
  label: string;
  value: ReactNode;
  color?: string;
}) {
  return (
    <Group justify="space-between" gap={12} wrap="nowrap" align="flex-start">
      <Text fz={13} c={T.ink2}>
        {label}
      </Text>
      {/* A div: a pending value is a Skeleton, which is a div. See StatCard. */}
      <Text component="div" fz={14} fw={800} c={color} ta="right">
        {value}
      </Text>
    </Group>
  );
}

function RangeRail({ value, onChange }: { value: RangeKey; onChange: (v: RangeKey) => void }) {
  return <ChipRail value={value} onChange={onChange} data={RANGES} />;
}

function ChartPlaceholder({ height }: { height: number }) {
  return (
    <Box
      style={{
        height,
        borderRadius: 12,
        border: `1px dashed ${T.lineHard}`,
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <Text fz={13} c={T.ink4}>
        No series — the analytics warehouse is not wired
      </Text>
    </Box>
  );
}

const LEDE = (
  <>
    Ground truth from <Text component="span" ff="monospace">router_llm_usage</Text> — one row per
    served completion, billable and attributed alike. Cost is the provider's list price, not what an
    estate charged; the router does not price.
  </>
);

export { Pill };
